import { useEffect } from 'react';
import { getFocusableElements, nextFocusIndex } from '../utils/focusTrap';

/**
 * While `active`, traps Tab/Shift+Tab focus within the element referenced by `containerRef`,
 * moves focus inside on activation, and restores focus to the previously focused element
 * when deactivated.
 *
 * @param {React.RefObject<HTMLElement>} containerRef
 * @param {boolean} active
 * @param {React.RefObject<HTMLElement>} [initialFocusRef] element to focus first
 */
export default function useFocusTrap(containerRef, active, initialFocusRef) {
  useEffect(() => {
    if (!active) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Move focus inside: explicit target -> first focusable -> the container itself.
    const focusInitial = () => {
      if (initialFocusRef && initialFocusRef.current) {
        initialFocusRef.current.focus();
        return;
      }
      const focusable = getFocusableElements(container);
      if (focusable.length > 0) {
        focusable[0].focus();
      } else {
        container.focus();
      }
    };
    focusInitial();

    const handleKeyDown = (event) => {
      if (event.key !== 'Tab') return;
      const focusable = getFocusableElements(container);
      if (focusable.length === 0) {
        // Nothing to tab to — keep focus on the container.
        event.preventDefault();
        container.focus();
        return;
      }
      const currentIndex = focusable.indexOf(document.activeElement);
      const targetIndex = nextFocusIndex(currentIndex, focusable.length, event.shiftKey);
      // Only intercept at the boundaries (or when focus escaped the set); let the browser
      // handle the common in-between case.
      const atBoundary =
        currentIndex === -1 ||
        (event.shiftKey && currentIndex === 0) ||
        (!event.shiftKey && currentIndex === focusable.length - 1);
      if (atBoundary) {
        event.preventDefault();
        focusable[targetIndex].focus();
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    return () => {
      container.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [active, containerRef, initialFocusRef]);
}
